import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';

const Contact = () => {
  const [animationKey, setAnimationKey] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    investmentInterest: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimationKey(prev => prev + 1);
          }
        });
      },
      { threshold: 0.3 }
    );

    const section = document.getElementById('contact');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://vnxtmtvfztsykgnbcnwx.supabase.co/functions/v1/send-contact-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          investmentInterest: '',
          message: '',
        });
        // Auto-hide success message after 8 seconds
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 8000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success animation particles
  const particles = Array.from({ length: 12 }, (_, i) => i);

  return (
    <section id="contact" className="pt-8 sm:pt-12 lg:pt-16 pb-14 sm:pb-20 lg:pb-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left Side - Image */}
          <motion.div
            key={`contact-left-${animationKey}`}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden h-[500px] sm:h-[550px] lg:h-[600px]">
              <img
                src="https://public.readdy.ai/ai/img_res/1e1d4b651ba2a0f22309b4268ee8246a.jpg"
                alt="Dulair Office"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 lg:p-8">
                <div className="text-gold text-xs font-medium mb-1">EXCLUSIVE ACCESS</div>
                <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                  Partner<br />with<br />Excellence
                </h3>
              </div>
            </div>

            {/* Contact Info - Horizontal Layout */}
            <div className="mt-5 sm:mt-6 grid grid-cols-3 gap-3">
              <div className="bg-cream rounded-xl p-3 sm:p-4 text-center group hover:bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gold/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
                  <i className="ri-map-pin-line text-gold text-sm sm:text-base group-hover:scale-110 transition-transform duration-300"></i>
                </div>
                <h4 className="text-navy font-bold text-xs mb-1 group-hover:text-gold transition-colors duration-300">Office</h4>
                <p className="text-text-light text-[10px] sm:text-xs leading-relaxed">
                  Kings Head House<br />
                  15 London End<br />
                  Beaconsfield<br />
                  HP9 2HN
                </p>
              </div>
              <div className="bg-cream rounded-xl p-3 sm:p-4 text-center group hover:bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gold/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
                  <i className="ri-phone-line text-gold text-sm sm:text-base group-hover:scale-110 transition-transform duration-300"></i>
                </div>
                <h4 className="text-navy font-bold text-xs mb-1 group-hover:text-gold transition-colors duration-300">Phone</h4>
                <p className="text-text-light text-[10px] sm:text-xs">+44 203 151 0000</p>
              </div>
              <div className="bg-cream rounded-xl p-3 sm:p-4 text-center group hover:bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gold/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
                  <i className="ri-mail-line text-gold text-sm sm:text-base group-hover:scale-110 transition-transform duration-300"></i>
                </div>
                <h4 className="text-navy font-bold text-xs mb-1 group-hover:text-gold transition-colors duration-300">Email</h4>
                <p className="text-text-light text-[10px] sm:text-xs break-all">enquiries@dulairpfo.com</p>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            key={`contact-right-${animationKey}`}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="text-navy text-xs sm:text-sm font-serif tracking-wider mb-2 sm:mb-3">GET IN TOUCH</div>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-2 sm:mb-3">
              Schedule Your<br />Private Consultation
            </h2>
            <p className="text-text-light text-xs sm:text-sm mb-5 sm:mb-6 leading-relaxed">
              Begin your journey with Dulair Private Family Office. Our team is ready to discuss how we can help preserve and grow your family wealth across property, healthcare, and strategic investments.
            </p>

            <AnimatePresence mode="wait">
              {submitStatus === 'success' ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="relative bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-6 sm:p-10 text-center overflow-hidden"
                >
                  {/* Animated particles */}
                  {particles.map((i) => (
                    <motion.div
                      key={i}
                      initial={{ 
                        opacity: 0, 
                        scale: 0,
                        x: 0,
                        y: 0
                      }}
                      animate={{ 
                        opacity: [0, 1, 0],
                        scale: [0, 1, 0.5],
                        x: Math.cos(i * 30 * Math.PI / 180) * 100,
                        y: Math.sin(i * 30 * Math.PI / 180) * 100
                      }}
                      transition={{ 
                        duration: 1.5,
                        delay: 0.2 + i * 0.05,
                        ease: "easeOut"
                      }}
                      className="absolute left-1/2 top-1/2 w-3 h-3 rounded-full"
                      style={{
                        background: i % 3 === 0 ? '#C9A962' : i % 3 === 1 ? '#10B981' : '#14B8A6'
                      }}
                    />
                  ))}

                  {/* Success icon with animation */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ 
                      type: "spring",
                      stiffness: 200,
                      damping: 15,
                      delay: 0.2
                    }}
                    className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg"
                  >
                    <motion.i 
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5, duration: 0.3 }}
                      className="ri-check-line text-white text-4xl sm:text-5xl"
                    ></motion.i>
                  </motion.div>

                  {/* Success text */}
                  <motion.h3
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    className="font-serif text-2xl sm:text-3xl font-bold text-navy mb-3"
                  >
                    Thank You!
                  </motion.h3>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="text-emerald-700 text-base sm:text-lg mb-2"
                  >
                    Your enquiry has been received successfully.
                  </motion.p>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                    className="text-text-light text-sm sm:text-base"
                  >
                    Our team will contact you within 24 hours.
                  </motion.p>

                  {/* Decorative rings */}
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1.5, opacity: 0 }}
                    transition={{ delay: 0.3, duration: 1.5, ease: "easeOut" }}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 border-4 border-emerald-300 rounded-full"
                  />
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 2, opacity: 0 }}
                    transition={{ delay: 0.5, duration: 1.5, ease: "easeOut" }}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 border-4 border-gold/50 rounded-full"
                  />

                  {/* Send another message button */}
                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                    onClick={() => setSubmitStatus('idle')}
                    className="mt-6 px-6 py-3 bg-white text-navy text-sm font-semibold rounded-full border-2 border-navy/20 hover:border-gold hover:bg-gold/5 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    Send Another Message
                  </motion.button>
                </motion.div>
              ) : (
                <motion.form 
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  id="contact-form" 
                  data-readdy-form 
                  onSubmit={handleSubmit} 
                  className="space-y-3 sm:space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-navy text-xs font-semibold mb-1.5">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2.5 border-2 border-gray/20 rounded-xl focus:border-gold focus:outline-none transition-colors text-sm text-black"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label htmlFor="email" className="block text-navy text-xs font-semibold mb-1.5">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2.5 border-2 border-gray/20 rounded-xl focus:border-gold focus:outline-none transition-colors text-sm text-black"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-navy text-xs font-semibold mb-1.5">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2.5 border-2 border-gray/20 rounded-xl focus:border-gold focus:outline-none transition-colors text-sm text-black"
                        placeholder="+44 20 1234 5678"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="investmentInterest" className="block text-navy text-xs font-semibold mb-1.5">
                      Investment Interest *
                    </label>
                    <select
                      id="investmentInterest"
                      name="investmentInterest"
                      value={formData.investmentInterest}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2.5 border-2 border-gray/20 rounded-xl focus:border-gold focus:outline-none transition-colors text-sm cursor-pointer text-black"
                    >
                      <option value="">Select your area of interest</option>
                      <option value="Property Investments">Property Investments</option>
                      <option value="Healthcare Ventures">Healthcare Ventures</option>
                      <option value="Strategic Investments">Strategic Investments</option>
                      <option value="Comprehensive Portfolio Management">Comprehensive Portfolio Management</option>
                      <option value="General Enquiry">General Enquiry</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-navy text-xs font-semibold mb-1.5">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 border-2 border-gray/20 rounded-xl focus:border-gold focus:outline-none transition-colors text-sm resize-none text-black"
                      placeholder="Tell us about your investment goals and how we can assist you..."
                    ></textarea>
                    <div className="text-right text-xs text-text-light mt-1">
                      {formData.message.length}/500 characters
                    </div>
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    className="w-full px-6 py-3 bg-gold text-navy text-sm font-bold rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 border-2 border-navy/30 border-t-navy rounded-full mr-2"
                        />
                        Sending...
                      </>
                    ) : (
                      <>
                        Begin Your Journey
                        <i className="ri-arrow-right-line ml-2"></i>
                      </>
                    )}
                  </motion.button>

                  {submitStatus === 'error' && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 border-2 border-red-200 rounded-xl p-3 text-center"
                    >
                      <i className="ri-error-warning-line text-red-600 text-xl mb-1"></i>
                      <p className="text-red-800 font-semibold text-sm">Something went wrong.</p>
                      <p className="text-red-700 text-xs mt-1">Please try again later.</p>
                    </motion.div>
                  )}
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
